import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-ext-400.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-ext-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/inter/latin-ext-700.css";
import "./styles.css";
import type { Member } from "@opulancia/deposit-core";
import type { App } from "./app.js";
import { connect, type Connection } from "./connection.js";
import { h, notice, replace } from "./dom.js";
import { AddScreen } from "./views/add.js";
import { detailView } from "./views/detail.js";
import { libraryView } from "./views/library.js";
import { studioEditorView } from "./views/studio-editor.js";
import { studioListView, studioNewView } from "./views/studio-list.js";
import { icon } from "./icons.js";
import emblemUrl from "./assets/brand/embleme.jpg?url";

const root = document.getElementById("app")!;

/** The original emblem and the written name, as the brand references give them (docs/visual-references/brand). */
function wordmark(): HTMLElement {
  return h("a", { class: "brand", href: "#/atelier", "aria-label": "OPULANCIA, accueil" }, h("img", { src: emblemUrl, alt: "", width: "40", height: "40" }), h("span", { class: "wordmark" }, "OPULANCIA"));
}

function loginView(connection: Connection, onDone: () => void, error?: string): HTMLElement {
  if (connection.demo) {
    return h(
      "div",
      { class: "login" },
      wordmark(),
      h("h1", null, "Démonstration"),
      notice("info", "Données fictives", "Rien n'est envoyé : tout reste dans ce navigateur et disparaît au rechargement."),
      connection.demoMembers.map((m) => h("button", { class: "btn big", type: "button", onclick: async () => (await connection.signIn(m.user_id), onDone()) }, `Entrer comme ${m.display_name}`)),
    );
  }
  const email = h("input", { type: "email", autocomplete: "username", required: true });
  const password = h("input", { type: "password", autocomplete: "current-password", required: true });
  return h(
    "div",
    { class: "login" },
    wordmark(),
    h("h1", null, "Connexion"),
    h("p", { class: "muted", style: "margin:0" }, "Accès réservé aux membres. Pas d'inscription : les comptes sont créés par l'administrateur."),
    error ? notice("bad", "Connexion refusée", error) : null,
    h(
      "form",
      {
        onsubmit: async (e: Event) => {
          e.preventDefault();
          try {
            await connection.signIn(email.value, password.value);
            onDone();
          } catch (err) {
            replace(root, loginView(connection, onDone, err instanceof Error ? err.message : String(err)));
          }
        },
      },
      h("label", { class: "field" }, "E-mail", email),
      h("label", { class: "field" }, "Mot de passe", password),
      h("button", { class: "btn primary big", type: "submit" }, "Se connecter"),
    ),
  );
}

async function start(): Promise<void> {
  let connection: Connection;
  try {
    connection = connect();
  } catch (error) {
    replace(root, h("main", null, notice("bad", "Configuration absente", error instanceof Error ? error.message : String(error))));
    return;
  }
  let member: Member | null = await connection.backend.currentMember().catch(() => null);
  if (!member) {
    const signedIn = connection.demo ? false : await connection.backend.listMembers().then(() => true, () => false);
    if (signedIn && !connection.demo) {
      replace(root, h("div", { class: "login" }, wordmark(), notice("bad", "Accès réservé", "Ce compte n'est pas membre d'OPULANCIA."), h("button", { class: "btn", onclick: async () => (await connection.signOut(), location.reload()) }, "Se déconnecter")));
      return;
    }
    replace(root, loginView(connection, () => void start()));
    return;
  }
  const members = new Map((await connection.backend.listMembers()).map((m) => [m.user_id, m.display_name]));
  let busy = false;
  let leave: (() => void) | null = null;
  window.addEventListener("beforeunload", (e) => {
    if (busy) e.preventDefault();
  });
  const app: App = {
    connection,
    member,
    members,
    navigate: (hash) => (location.hash = hash),
    refresh: () => route(),
    setBusy: (b) => (busy = b),
    onLeave: (fn) => (leave = fn),
  };
  const add = new AddScreen(app);
  const main = h("main", { id: "main" });
  const link = (route: string, label: string, ic: Parameters<typeof icon>[0]) => h("a", { href: `#/${route}`, "data-route": route }, icon(ic), h("span", null, label));
  const nav = h("nav", { class: "nav", "aria-label": "Navigation" }, link("atelier", "Atelier", "grid"), link("bibliotheque", "Bibliothèque", "library"));
  const bottom = h("nav", { class: "bottomnav", "aria-label": "Navigation mobile" }, link("atelier", "Atelier", "grid"), link("bibliotheque", "Bibliothèque", "library"), link("ajouter", "Déposer", "plus"));
  const shell = h(
    "div",
    { class: "shell" },
    h(
      "aside",
      { class: "sidebar" },
      wordmark(),
      connection.demo ? h("span", { class: "demo-flag" }, "DÉMO FICTIVE") : null,
      nav,
      h("a", { class: "btn deposit", href: "#/ajouter", "data-route": "ajouter" }, icon("plus"), h("span", null, "Déposer")),
      h(
        "div",
        { class: "who" },
        h("span", { class: "avatar", "aria-hidden": "true" }, member.display_name.slice(0, 1).toUpperCase()),
        h("span", { class: "who-name" }, member.display_name),
        h("button", { type: "button", "aria-label": "Déconnexion", title: "Déconnexion", onclick: async () => (await connection.signOut(), (location.hash = ""), location.reload()) }, icon("logout"), h("span", { class: "who-out" }, "Déconnexion")),
      ),
    ),
    main,
  );
  replace(root, h("a", { class: "sr-only", href: "#main" }, "Aller au contenu"), shell, bottom);

  function route(): void {
    const [, page = "atelier", id] = location.hash.replace(/^#\/?/, "#/").split("/");
    const current = page === "depot" ? "bibliotheque" : page;
    for (const a of [...shell.querySelectorAll<HTMLAnchorElement>("a[data-route]"), ...bottom.querySelectorAll<HTMLAnchorElement>("a[data-route]")]) {
      if (a.dataset["route"] === current) a.setAttribute("aria-current", "page");
      else a.removeAttribute("aria-current");
    }
    const left = leave;
    leave = null;
    left?.();
    const editing = page === "atelier" && !!id && id !== "nouveau";
    main.classList.toggle("wide", editing);
    shell.classList.toggle("editing", editing);
    document.body.classList.toggle("editing", editing);
    replace(
      main,
      page === "bibliotheque"
        ? libraryView(app)
        : page === "depot" && id
          ? detailView(app, id)
          : page === "atelier"
            ? id === "nouveau"
              ? studioNewView(app)
              : id
                ? studioEditorView(app, id)
                : studioListView(app)
            : page === "ajouter"
              ? add.render()
              : studioListView(app),
    );
    window.scrollTo({ top: 0 });
  }
  window.addEventListener("hashchange", route);
  route();
}

void start();
